import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import axios from '../../utils/axios';

const ExamHistory = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchResults = async () => {
            try {
                const response = await axios.get('/api/exams/results');
                setResults(response.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to load exam results');
                setLoading(false);
            }
        };

        fetchResults();
    }, []);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (seconds) => {
        if (!seconds) return 'Not recorded';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md">
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 4 }}>
                Exam History
            </Typography>
            
            {results.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1">
                        You haven't taken any exams yet.
                    </Typography>
                </Paper>
            ) : (
                <List sx={{ p: 0 }}>
                    {results.map((result, index) => (
                        <Paper 
                            elevation={1} 
                            sx={{ 
                                mb: 2, 
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                            }} 
                            key={result._id}
                        >
                            <ListItem>
                                <ListItemText
                                    primary={result.exam.title}
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2" color="text.primary">
                                                Score: {Math.round(result.score)}%
                                            </Typography>
                                            <br />
                                            <Typography component="span" variant="body2" color="text.secondary">
                                                {formatDate(result.completedAt)}
                                            </Typography>
                                            {result.duration > 0 && (
                                                <>
                                                    <br />
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        Time taken: {formatTime(result.duration)}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    }
                                    secondaryTypographyProps={{ component: 'div' }}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        aria-label="view"
                                        onClick={() => navigate(`/exams/results/${result._id}`)}
                                    >
                                        <ViewIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </Paper>
                    ))}
                </List>
            )}
        </Container>
    );
};

export default ExamHistory; 